// src/App.tsx
import React, { useState, useEffect } from "react";
import { SidebarKernelSelector } from "./components/SidebarKernelSelector";
import { Header } from "./components/Header";
import { ParameterGraph } from "./components/ParameterGraph";
import { PolygonPhaseChart } from "./components/PolygonPhaseChart";
import { LeniaViewer } from "./components/LeniaViewer";
import { usePyodide } from "./hooks/usePyodide";
import { KernelConfig, ParamSelection, PolygonSelection, PhaseData } from "./types/lenia";
import { KernelTopBar } from "./components/KernelTopBar";

import {
  listKernels,
  loadPhaseGrid,
  pickInitialSelection,
} from "./utils/leniaDataAdapter";

export default function App() {
  const [kernelConfigs, setKernelConfigs]   = useState<KernelConfig[]>([]);
  const [selectedKernel, setSelectedKernel] = useState<KernelConfig | null>(null);
  const [selectedParams, setSelectedParams] = useState<ParamSelection | null>(null);
  const [polygonSelection, setPolygonSelection] = useState<PolygonSelection | null>(null);
  const [phaseData, setPhaseData] = useState<PhaseData[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [polygonLoading, setPolygonLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { pyodide, isLoaded: pyodideLoaded, error: pyodideError } = usePyodide();

  // Load kernels
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const kernels = await listKernels(ac.signal);
        setKernelConfigs(kernels);
        if (kernels.length) setSelectedKernel(kernels[0]);
      } catch (e) {
        setError("Failed to load kernel configurations");
      } finally {
        setInitialLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // Load phase grid when kernel changes
  useEffect(() => {
    if (!selectedKernel) return;
    const ac = new AbortController();
    setError(null);
    setPhaseData([]);
    setSelectedParams(null);
    setPolygonSelection(null);

    (async () => {
      try {
        const data = await loadPhaseGrid(selectedKernel, ac.signal);
        setPhaseData(data);
        const initial = pickInitialSelection(data);
        setSelectedParams(initial);
      } catch (e) {
        setError("Failed to load phase data");
      }
    })();

    return () => ac.abort();
  }, [selectedKernel]);

  // If phaseData empties (e.g., kernel switch), reset picks.
  useEffect(() => {
    if (!phaseData.length) {
      setSelectedParams(null);
      setPolygonSelection(null);
    }
  }, [phaseData]);

  const handleKernelChange = (kernel: KernelConfig) => {
    setSelectedKernel(kernel);
  };

  const handleParamSelect = (params: ParamSelection) => {
    setSelectedParams(params);
    setPolygonSelection(null);
  };

  const handlePolygonSelect = (selection: PolygonSelection) => {
    setPolygonSelection(selection);
  };

  if (initialLoading) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        color: "#e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.2rem"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: "1rem" }}>Loading kernel configurations...</div>
          {!pyodideLoaded && !pyodideError && (
            <div style={{
              fontSize: "0.9rem",
              color: "#94a3b8",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}>
              <div style={{
                width: "1rem",
                height: "1rem",
                border: "0.125rem solid transparent",
                borderTop: "0.125rem solid #60a5fa",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }} />
              Preparing simulation engine in background...
            </div>
          )}
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        color: "#e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.2rem"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#ef4444", marginBottom: "1rem" }}>Error</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" }}>
      <SidebarKernelSelector
        kernels={kernelConfigs}
        selectedKernel={selectedKernel}
        onSelect={handleKernelChange}
      />
      <div className="app-shell">
        <div className="center-wrap">
          <Header />
          <div className="main-grid" style={{ paddingTop: "1.5rem" }}>
            <div style={{ gridArea: "left" }}>
              {selectedKernel && (
                <KernelTopBar
                  kernels={kernelConfigs}
                  selectedKernel={selectedKernel}
                  onSelect={handleKernelChange}
                />
              )}
              {selectedKernel && (
                <ParameterGraph
                  phaseData={phaseData}
                  onParamSelect={handleParamSelect}
                  selectedParams={selectedParams}
                />
              )}
            </div>
            <div style={{ gridArea: "right" }} className="right-stack">
              {selectedParams && selectedKernel && (
                <LeniaViewer
                  kernel={selectedKernel}
                  params={selectedParams}
                  polygonSelection={polygonSelection}
                  selectionLoading={polygonLoading}
                  pyodide={pyodide}
                  pyodideLoaded={pyodideLoaded}
                  pyodideError={pyodideError}
                />
              )}
              {selectedParams && selectedKernel && (
                <div className="polygraph-wrap">
                  <PolygonPhaseChart
                    kernel={selectedKernel}
                    params={selectedParams}
                    onPolygonSelect={handlePolygonSelect}
                    selectedPolygon={polygonSelection}
                    setLoading={setPolygonLoading}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        :root { --sidebar-collapsed: 3.875rem; } /* 62px */
        .app-shell { padding-left: calc(var(--sidebar-collapsed) + 1rem); }
        .center-wrap { max-width: 81.25rem; margin: 0 auto; padding: 0 2rem 2rem; }
        .main-grid {
          display: grid; grid-template-areas: "left right";
          grid-template-columns: minmax(0, 5fr) minmax(0, 3fr);
          column-gap: 1.5rem; row-gap: 0;
        }
        .right-stack { display: grid; grid-auto-rows: min-content; row-gap: 0.75rem; }
        .polygraph-wrap { opacity: 0.95; }
        @media (min-width: 87.5rem) {
          .center-wrap { max-width: 180rem; padding-left: 2.25rem; padding-right: 2.25rem; }
          .main-grid { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); column-gap: 1.75rem; }
        }
        @media (max-width: 63.75rem) {
          :root { --sidebar-collapsed: 3.5rem; }
          .main-grid { grid-template-areas: "left" "right"; grid-template-columns: 1fr; row-gap: 1rem; }
          .center-wrap { padding: 0 1.25rem 1.25rem; }
        }
        @media (max-width: 63.75rem) {
          :root { --sidebar-collapsed: 0; }
          .center-wrap { padding: 0 1rem 1rem; }
        }
      `}</style>
    </div>
  );
}
